import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Compass, ShieldAlert } from 'lucide-react';

export default function Contact() {
  const [contacts, setContacts] = useState([]);
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/GetContactInfo`)
      .then(res => setContacts(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error(err);
        setContacts([]);
      });
  }, []);

  const schoolContact = (Array.isArray(contacts) && contacts[0]) || {
    Email: 'technoschoolbsp@gmail.com',
    PhoneNo: '8982397404',
    Location: 'Daya Vihar, Ganesh Nagar, Bilaspur (C.G.) - 495 004'
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formState.name || !formState.email || !formState.message) return;
    setSubmitted(true);

    axios.post(`${API_BASE_URL}/AddMessage`, formState)
      .then(() => {
        setFormState({ name: '', email: '', message: '' });
        setSubmitted(false);
        alert('Thank you for reaching out! We will contact you shortly.');
      })
      .catch(err => {
        console.error(err);
        setSubmitted(false);
        alert('There was an error sending your message. Please try again.');
      });
  };

  const quickActions = [
    { title: "Call School", value: `+91 ${schoolContact.PhoneNo}`, icon: <Phone size={20} />, href: `tel:${schoolContact.PhoneNo}`, color: 'var(--primary-color)' },
    { title: "WhatsApp Direct", value: "Message Support", icon: <MessageSquare size={20} />, href: `https://wa.me/91${schoolContact.PhoneNo}`, color: 'var(--accent-green)' },
    { title: "Official Email", value: schoolContact.Email, icon: <Mail size={20} />, href: `mailto:${schoolContact.Email}`, color: 'var(--accent-purple)' },
    { title: "YouTube Channel", value: "Watch Our Videos", icon: <i className="fa-brands fa-youtube" style={{ fontSize: '20px' }} />, href: "https://www.youtube.com/channel/UCsQisHLHtKsIbPFQRpZUZew", color: '#FF0000' },
    { title: "Instagram Page", value: "@technoschool_bsp", icon: <i className="fa-brands fa-instagram" style={{ fontSize: '20px' }} />, href: "https://www.instagram.com/technoschool_bsp/", color: '#E1306C' },
    { title: "Get Directions", value: "Open Google Maps", icon: <Compass size={20} />, href: "https://maps.app.goo.gl/cstLpnvmpNTHA3c5A", color: 'var(--accent-coral)' }
  ];

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-contact)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative', overflow: 'hidden' }}>
      
      {/* Playful background floating doodles */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>

      <div className="page-container-wide" style={{ position: 'relative', zIndex: 10 }}>
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: '50px' }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary-dark)', textTransform: 'uppercase', letterSpacing: '1px' }}>Get In Touch</span>
          <h1 style={{ fontSize: '3rem', color: 'var(--text-primary)', marginBottom: '8px', fontFamily: 'Poppins', fontWeight: 800 }}>Contact Us</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 700, fontSize: '1.05rem' }}>We are always here to help you. Reach out for queries or plan a visit.</p>
        </motion.div>

        {/* Quick Action Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '24px', marginBottom: '56px' }}>
          {quickActions.map((act, i) => (
            <motion.a 
              key={i} 
              href={act.href} 
              target={act.href.startsWith('http') ? '_blank' : '_self'} 
              rel="noreferrer" 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{ ...actionCardStyle, borderTop: `6px solid ${act.color}` }}
            >
              <div style={{ ...actionIconStyle, color: act.color }}>{act.icon}</div>
              <h4 style={{ fontSize: '1.2rem', color: 'var(--text-primary)', margin: '0 0 6px', fontWeight: 800, fontFamily: 'Poppins' }}>{act.title}</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{act.value}</p>
            </motion.a>
          ))}
        </div>

        {/* Split Section: Form and Info */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '48px', alignItems: 'start', marginBottom: '48px' }}>
          
          {/* Contact Details Column */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
          >
            <div style={infoCardStyle}>
              <h3 style={infoTitleStyle}><MapPin size={18} style={{ color: 'var(--accent-coral)' }} /> School Location</h3>
              <p style={infoTextStyle}>{schoolContact.Location}</p>
            </div>

            <div style={infoCardStyle}>
              <h3 style={infoTitleStyle}><Clock size={18} style={{ color: 'var(--accent-purple)' }} /> Office Hours</h3>
              <p style={infoTextStyle}>Monday – Saturday: 8:00 AM – 2:00 PM</p>
              <p style={{ ...infoTextStyle, color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '4px' }}>Note: Closed on Sundays and Public Holidays</p>
            </div>

            <div style={{ ...infoCardStyle, backgroundColor: '#fef2f2', borderColor: '#fee2e2', borderTop: '6px solid var(--danger-color)' }}>
              <h3 style={{ ...infoTitleStyle, color: 'var(--danger-color)' }}><ShieldAlert size={18} /> Administrative Helpline</h3>
              <p style={infoTextStyle}>For quick assistance, call: +91 {schoolContact.PhoneNo}</p>
            </div>

            {/* Direct Google Maps iframe embed */}
            <div style={{ borderRadius: 'var(--border-radius-card)', overflow: 'hidden', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)', height: '240px' }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m10!1m8!1m3!1d423.91866590905835!2d82.18289922576086!3d22.049919866847382!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1784137884345!5m2!1sen!2sin" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy"
                title="Direct Map Location"
              />
            </div>
          </motion.div>

          {/* Interactive Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            style={{ ...formCardStyle, borderTop: '8px solid var(--primary-color)', boxShadow: '0 20px 48px rgba(96, 165, 250, 0.12)' }}
          >
            <h3 style={{ fontSize: '1.5rem', color: 'var(--text-primary)', marginBottom: '8px', fontWeight: 800, fontFamily: 'Poppins' }}>Send Us a Message</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '24px', fontWeight: 600 }}>We usually reply within 24 hours.</p>
            <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label style={labelStyle}>Your Name *</label>
                <input 
                  type="text" 
                  value={formState.name} 
                  onChange={(e) => setFormState({ ...formState, name: e.target.value })} 
                  className="contact-custom-input" 
                  placeholder="Enter your full name" 
                  required 
                />
              </div>

              <div>
                <label style={labelStyle}>Email Address *</label>
                <input 
                  type="email" 
                  value={formState.email} 
                  onChange={(e) => setFormState({ ...formState, email: e.target.value })} 
                  className="contact-custom-input" 
                  placeholder="parent@example.com" 
                  required 
                />
              </div>

              <div>
                <label style={labelStyle}>Your Message / Query *</label>
                <textarea 
                  value={formState.message} 
                  onChange={(e) => setFormState({ ...formState, message: e.target.value })} 
                  className="contact-custom-input" 
                  rows="5" 
                  placeholder="Write your query or question here..." 
                  style={{ resize: 'vertical' }}
                  required 
                />
              </div>

              <button 
                type="submit" 
                className="btn-premium btn-premium-primary"
                style={{ height: '52px', fontSize: '1.02rem', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', width: '100%', marginTop: '8px' }}
                disabled={submitted}
              >
                <Send size={18} /> {submitted ? 'Sending details...' : 'Submit Message'}
              </button>
            </form>
          </motion.div>

        </div>

      </div>
    </div>
  );
}

// Inline Styles
const actionCardStyle = {
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-md)',
  textAlign: 'center',
  textDecoration: 'none',
  transition: 'transform var(--transition-normal), border-color var(--transition-normal)'
};

const actionIconStyle = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  backgroundColor: 'var(--bg-color)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto 16px',
  boxShadow: 'var(--shadow-sm)'
};

const infoCardStyle = {
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-md)'
};

const infoTitleStyle = {
  fontSize: '1.15rem',
  color: 'var(--text-primary)',
  margin: '0 0 10px',
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontFamily: 'Poppins'
};

const infoTextStyle = {
  margin: 0,
  fontSize: '0.95rem',
  color: 'var(--text-secondary)',
  lineHeight: '1.6',
  fontWeight: 600
};

const formCardStyle = {
  backgroundColor: '#ffffff',
  padding: '40px 32px',
  borderRadius: 'var(--border-radius-card)',
  border: '1px solid var(--border-color)',
  boxShadow: 'var(--shadow-lg)'
};

const labelStyle = {
  display: 'block',
  fontSize: '0.85rem',
  fontWeight: 700,
  color: 'var(--text-secondary)',
  marginBottom: '6px'
};

// Add standard triggers
const hoverStyles = document.createElement('style');
hoverStyles.innerHTML = `
  a[style*="text-decoration: none"]:hover {
    transform: translateY(-6px) !important;
    border-color: var(--primary-color) !important;
  }
  .contact-custom-input {
    width: 100%;
    padding: 14px 20px;
    font-size: 0.98rem;
    font-weight: 700;
    font-family: 'Quicksand', sans-serif;
    border: 2px solid var(--border-color);
    border-radius: var(--border-radius-form);
    background-color: #f8fafc;
    color: var(--text-primary);
    outline: none;
    transition: all 0.25s ease;
  }
  .contact-custom-input:focus {
    border-color: var(--primary-color) !important;
    background-color: #ffffff !important;
    box-shadow: 0 0 0 4px rgba(96, 165, 250, 0.15) !important;
  }
`;
document.head.appendChild(hoverStyles);
